import { api, schedulingPath } from "./api";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import {
  InfoBoardCategory,
  InfoBoardItemLink,
  InfoBoardOverview,
  InfoBoardUploadRequest,
  InfoBoardUploadResponse,
} from "../types/infoboard";
import QueryKeys from "./queryKeys";

class InfoBoardAPI {
  static base = schedulingPath("/infoboard");
  static category = `${InfoBoardAPI.base}/category`;
  static link = `${InfoBoardAPI.base}/link`;

  async getOverview(langGroupId: number): Promise<InfoBoardOverview> {
    return api.get(InfoBoardAPI.base, { searchParams: { lgroup: langGroupId } }).json();
  }

  async getUploadUrl(langGroupId: number, uploadRequest: InfoBoardUploadRequest): Promise<InfoBoardUploadResponse> {
    return api.post(InfoBoardAPI.base, { json: uploadRequest, searchParams: { lgroup: langGroupId } }).json();
  }

  async updateLink(langGroupId: number, link: InfoBoardItemLink) {
    return api.put(`${InfoBoardAPI.link}`, { json: link, searchParams: { lgroup: langGroupId } }).json();
  }

  async deleteLink(langGroupId: number, linkId: number) {
    await api.delete(`${InfoBoardAPI.link}/${linkId}`, { searchParams: { lgroup: langGroupId } });
  }

  async addCategory(langGroupId: number, category: InfoBoardCategory): Promise<InfoBoardCategory> {
    return api.post(InfoBoardAPI.category, { json: category, searchParams: { lgroup: langGroupId } }).json();
  }

  async updateCategory(langGroupId: number, category: InfoBoardCategory): Promise<InfoBoardCategory> {
    return api.put(InfoBoardAPI.category, { json: category, searchParams: { lgroup: langGroupId } }).json();
  }

  async deleteCategory(langGroupId: number, categoryId: number) {
    await api.delete(`${InfoBoardAPI.category}/${categoryId}`, { searchParams: { lgroup: langGroupId } });
  }

  async updateItemLinksSortOrder(langGroupId: number, sortOrderMap: Map<number, number>): Promise<InfoBoardItemLink[]> {
    const sortOrderArray = Array.from(sortOrderMap.entries()).map(([id, sortOrder]) => ({ id, sortOrder }));
    return api
      .put(`${InfoBoardAPI.link}/sortOrder`, {
        json: sortOrderArray,
        searchParams: { lgroup: langGroupId },
      })
      .json();
  }

  async updateCategoriesSortOrder(
    langGroupId: number,
    sortOrderMap: Map<number, number>,
  ): Promise<InfoBoardCategory[]> {
    const sortOrderArray = Array.from(sortOrderMap.entries()).map(([id, sortOrder]) => ({ id, sortOrder }));
    return api
      .put(`${InfoBoardAPI.category}/sortOrder`, {
        json: sortOrderArray,
        searchParams: { lgroup: langGroupId },
      })
      .json();
  }

  async notify(langGroupId: number): Promise<void> {
    await api.post(`${InfoBoardAPI.base}/notify`, { searchParams: { lgroup: langGroupId } });
  }
}

export function useInfoBoard(langGroupId: number, options?: Partial<UseQueryOptions<InfoBoardOverview, Error>>) {
  return useQuery({
    queryKey: [QueryKeys.InfoBoard, langGroupId],
    queryFn: () => infoBoardApi.getOverview(langGroupId),
    ...options,
  });
}

export const infoBoardApi = new InfoBoardAPI();
